import { Form, PasswordField, SubmitButton, Text } from '@/components';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { z } from 'zod';

import { useInitializePassword } from '../_hooks/initialize-password';
import { InitializePasswordFormSchema } from '../_models/initialize-password';

type FormValues = z.infer<typeof InitializePasswordFormSchema>;

type Props = {
  token: string;
};

export const InitializePasswordForm: FC<Props> = ({ token }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openMessage } = useMessage();

  const { mutateAsync: initializePasswordAsync } = useInitializePassword();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
  } = useForm<FormValues>({
    resolver: zodResolver(InitializePasswordFormSchema),
  });

  const onSubmit = useCallback(
    async ({ newPassword }: FormValues) => {
      await initializePasswordAsync({
        token,
        newPassword,
      });
      openMessage('success', t('message-password-update-completed'));
      navigate('/login', { replace: true });
    },
    [token, initializePasswordAsync, openMessage, navigate, t],
  );

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Form.Item>
        <Text text={t('password-initialization-message-set-a-new-password')} />
      </Form.Item>
      <PasswordField
        label={t('new-password')}
        name="newPassword"
        control={control}
        errors={errors}
        showHint
        required
      />
      <PasswordField
        name="confirmPassword"
        label={t('confirm-password')}
        control={control}
        errors={errors}
        required
      />
      <Form.Item>
        <SubmitButton
          disabled={!isDirty}
          loading={isSubmitting}
          text={t('send')}
        />
      </Form.Item>
    </Form>
  );
};
